export interface SegmentRow {
  id: string;
  lineItemGroupId?: string;
  endDate?: Date | string | null;
  startDate?: Date | string;
}

/** Latest open segment in a recurring line-item chain. */
export function getOpenSegmentTarget<T extends SegmentRow>(
  item: T,
  items: T[],
): T | null {
  const groupId = item.lineItemGroupId ?? item.id;
  const openSegments = items
    .filter(
      (candidate) =>
        (candidate.lineItemGroupId ?? candidate.id) === groupId && !candidate.endDate,
    )
    .sort((a, b) => {
      const aStart = new Date(a.startDate ?? 0).getTime();
      const bStart = new Date(b.startDate ?? 0).getTime();
      return aStart - bStart;
    });

  return openSegments[openSegments.length - 1] ?? null;
}

export function canReviseSegment(item: SegmentRow, items: SegmentRow[]): boolean {
  const target = getOpenSegmentTarget(item, items);
  return target?.id === item.id;
}

/** True when a newer segment exists in the same chain (editing amounts affects history). */
export function hasLaterSegments(item: SegmentRow, items: SegmentRow[]): boolean {
  const target = getOpenSegmentTarget(item, items);
  return Boolean(target && target.id !== item.id);
}

export function isLatestRevisionSegment(
  item: SegmentRow & { segmentKind?: string; revisesId?: string },
  items: SegmentRow[],
): boolean {
  const open = getOpenSegmentTarget(item, items);
  return (
    open?.id === item.id &&
    item.segmentKind === "revision" &&
    Boolean(item.revisesId)
  );
}

export function confirmEditSupersededSegment(itemLabel: string): boolean {
  return window.confirm(
    `"${itemLabel}" has later revisions. Changing amounts or rates on this segment will rewrite history for those months. Continue?`,
  );
}
